import CustomTable from "../../../components/CustomTable/CustomTable"
import ColumnSettingsDropdown from "../../../components/CustomTable/ColumnSettingsDropdown";
import { Flex, Input, Modal, message } from "antd";
import { useSelector, useDispatch } from "react-redux";
import useSearchableTable from "../../../hooks/useSearchableTable";
import { transactionHistory, disputeRaised, rejectTxn, pendingTxn, suspenseTxn, markTransactionDuplicate } from "../../../redux/slices/PaymentsSlice";
import { useState, useEffect, useMemo, useCallback } from "react";
import { getPaymentColumns } from "./getPaymentColumns";
import { useNavigate } from "react-router-dom";
import { REASON_MODAL_CONFIG } from "../../../constants/constants";
import CustomModal from "../../../components/CustomModal/CustomModal";
import ReasonForm from "../../../components/CustomForms/ReasonForm";
import {
  TABLE_COLUMN_DEFAULTS,
  buildTableScreenKey,
} from "../../../constants/tableColumnDefaults";

const PaymentsTable = ({ data, tabIndex, canMarkDuplicate = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading } = useSelector((state) => state?.payments);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10
  })
  const [modal, setModal] = useState({ type: null, data: null });
  const [columnControl, setColumnControl] = useState(null);

  const screenKey = useMemo(
    () => buildTableScreenKey("payments", String(tabIndex)),
    [tabIndex],
  );

  const { searchText, setSearchText, filteredData } =
    useSearchableTable(data?.data || []);

  const closeModal = useCallback(() => setModal({ type: null, data: null }), []);

  const openModal = useCallback((type, record) => {
    setModal({ type, data: record });
  }, []);

  // Pick total from API response based on tab
  const total = data?.total || 0;

  useEffect(() => {
    // When tab changes, reset pagination AND fetch immediately with page 1
    const params = { page: 1, pageSize: pagination.pageSize };
    setPagination(params);

    if (tabIndex === 1) dispatch(transactionHistory(params));
    else if (tabIndex === 2) dispatch(disputeRaised(params));
    else if (tabIndex === 3) dispatch(rejectTxn(params));
    else if (tabIndex === 4) dispatch(pendingTxn(params));
    else if (tabIndex === 5) dispatch(suspenseTxn(params));
  }, [dispatch, pagination.pageSize, tabIndex]);


  useEffect(() => {
  const params = { page: pagination.page, pageSize: pagination.pageSize };
  if (!tabIndex) return;
  if (tabIndex === 1) dispatch(transactionHistory(params));
  else if (tabIndex === 2) dispatch(disputeRaised(params));
  else if (tabIndex === 3) dispatch(rejectTxn(params));
  else if (tabIndex === 4) dispatch(pendingTxn(params));
  else if (tabIndex === 5) dispatch(suspenseTxn(params));
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [dispatch, pagination.page, pagination.pageSize]);


  const columns = useMemo(
    () =>
      getPaymentColumns({
        tabIndex,
        canMarkDuplicate,
        onView: (id) => navigate(`/payments/payment-details/${id}/${tabIndex}`),
        onApprove: (record) => openModal("approve", record),
        onReject: (record) => openModal("reject", record),
        onMarkDuplicate: (record) => {
          Modal.confirm({
            title: "Mark as Duplicate",
            content: `Mark ${record.appTransactionId} (${record.txnChannel || "transaction"}) as Duplicate? Wallet and ledger will exclude this amount.`,
            okText: "Mark as Duplicate",
            onOk: async () => {
              const result = await dispatch(markTransactionDuplicate({
                transactionId: record.appTransactionId,
              }));
              if (markTransactionDuplicate.fulfilled.match(result)) {
                message.success("Transaction marked as Duplicate");
                dispatch(transactionHistory({
                  page: pagination.page,
                  pageSize: pagination.pageSize,
                }));
              } else {
                message.error(result.payload || "Failed to mark transaction as Duplicate");
              }
            },
          });
        },
      }),
    [canMarkDuplicate, dispatch, navigate, openModal, pagination.page, pagination.pageSize, tabIndex]
  );

  const handleTableChange = (newPagination) => {
    setPagination({
      page: newPagination.current,
      pageSize: newPagination.pageSize,
    });
  };

  const handleReasonSubmit = () => { }

  return (
    <>
      <Flex
        gap={8}
        justify="flex-end"
        align="center"
        className="table-toolbar-controls"
        style={{ marginRight: 20, marginBottom: 8 }}
      >
        {columnControl && (
          <ColumnSettingsDropdown
            availableOptions={columnControl.availableOptions}
            visibleOrderedKeys={columnControl.visibleOrderedKeys}
            onToggleColumn={columnControl.onToggleColumn}
            onToggleAll={columnControl.onToggleAll}
            onReorder={columnControl.onReorder}
            onReset={columnControl.onReset}
            loading={columnControl.loading}
            saving={columnControl.saving}
            isCustomized={columnControl.isCustomized}
          />
        )}
        <Input
          placeholder="Search"
          allowClear
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="search-input"
          style={{ width: 250 }}
        />
      </Flex>
      <CustomTable
        rowKey={tabIndex === 1 ? "appTransactionId" : tabIndex === 5 ? "transactionId" : "txnId"}
        columns={columns}
        dataSource={filteredData}
        loading={loading}
        scroll={{ x: 'max-content' }}
        pagination={{
          current: pagination.page,
          pageSize: pagination.pageSize,
          total: total,
        }}
        onChange={handleTableChange}
        screenKey={screenKey}
        defaultColumnKeys={TABLE_COLUMN_DEFAULTS.payments}
        onColumnControlChange={setColumnControl}
      />
      {modal.type && (
        <CustomModal
          title={`${modal.type.charAt(0).toUpperCase() + modal.type.slice(1)
            } Transaction`}
          open
          centered
          content={
            <ReasonForm
              label={REASON_MODAL_CONFIG[modal.type]?.label}
              placeholder="Write a valid reason"
              btnName={REASON_MODAL_CONFIG[modal.type]?.btnName}
              btnType={modal.type === "reject" ? "danger" : "primary"}
              submitLoading={false}
              submitError=""
              onClose={closeModal}
              handleOnFormSubmit={handleReasonSubmit}
            />
          }
        />
      )}
    </>
  )
}

export default PaymentsTable